import { useStore } from '@hooks/useStore'
import { Search } from '@mui/icons-material'
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { IKvkCompany } from '@api'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ISingleCompany } from '@typings'
import { useKvkPrefilled } from '../client/client.kvk.search'

export const CompanyKvkSearch = observer(() => {
  const { kvk } = useStore()
  const { setValue, getValues, reset } = useFormContext<{ company: ISingleCompany }>()
  const { markFieldAsPrefilled } = useKvkPrefilled()
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState<IKvkCompany | null>(null)
  // Track last selected KVK number for race condition protection
  const lastSelectedKvkRef = useRef<string | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue) {
        setSearchQuery(inputValue)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [inputValue])

  // Trigger search when query changes
  useEffect(() => {
    if (searchQuery) {
      kvk.searchCompany(searchQuery)
    }
  }, [searchQuery])

  const handleSelectCompany = async (company: IKvkCompany | null) => {
    if (!company) return

    // Track for race condition protection
    lastSelectedKvkRef.current = company.kvk_number

    // Step 1: Optimistic population from search data (immediate)
    if (company.name) {
      setValue('company.name', company.name, { shouldDirty: true })
      markFieldAsPrefilled('company.name')
    }

    if (company.street) {
      setValue('company.street', company.street, { shouldDirty: true })
      markFieldAsPrefilled('company.street')
    }

    if (company.house_number) {
      setValue('company.house_number', company.house_number, { shouldDirty: true })
      markFieldAsPrefilled('company.house_number')
    }

    if (company.house_number_addition) {
      setValue('company.house_number_addition', company.house_number_addition, { shouldDirty: true })
      markFieldAsPrefilled('company.house_number_addition')
    }

    if (company.postal_code) {
      setValue('company.postal_code', company.postal_code, { shouldDirty: true })
      markFieldAsPrefilled('company.postal_code')
    }

    if (company.city) {
      setValue('company.city', company.city, { shouldDirty: true })
      markFieldAsPrefilled('company.city')
    }

    if (company.kvk_number) {
      setValue('company.coc', company.kvk_number, { shouldDirty: true })
      markFieldAsPrefilled('company.coc')
    }

    // BTW field NOT populated - KVK does not return BTW numbers
    // Company form uses hardcoded "Nederland" country - no country enrichment needed
    // since company form doesn't have a country selector

    // Force re-render by resetting form with all current values
    setTimeout(() => {
      const currentValues = getValues()
      reset(currentValues, { keepDirty: true })
    }, 10)

    // Clear search
    setInputValue('')
    setSearchQuery('')
    setSelectedValue(null)

    // Step 2: Fire detail lookup to upgrade with richer data
    try {
      if (!company.kvk_number) return
      const details = await kvk.getCompanyByKvkNumber(company.kvk_number)

      // Only apply if still the selected company (race condition check)
      if (!details || lastSelectedKvkRef.current !== company.kvk_number) return

      if (details.street) {
        setValue('company.street', details.street, { shouldDirty: true })
        markFieldAsPrefilled('company.street')
      }

      if (details.house_number) {
        setValue('company.house_number', details.house_number, { shouldDirty: true })
        markFieldAsPrefilled('company.house_number')
      }

      // Combine house_number_addition with toevoeging_adres if both present
      if (details.house_number_addition || details.toevoeging_adres) {
        const baseAddition = details.house_number_addition || ''
        const toevoeging = details.toevoeging_adres || ''
        const combined = [baseAddition, toevoeging].filter(Boolean).join(' ').trim()
        if (combined) {
          setValue('company.house_number_addition', combined, { shouldDirty: true })
          markFieldAsPrefilled('company.house_number_addition')
        }
      }

      if (details.postal_code) {
        setValue('company.postal_code', details.postal_code, { shouldDirty: true })
        markFieldAsPrefilled('company.postal_code')
      }

      if (details.city) {
        setValue('company.city', details.city, { shouldDirty: true })
        markFieldAsPrefilled('company.city')
      }

      // Refresh form with enriched values
      setTimeout(() => {
        const currentValues = getValues()
        reset(currentValues, { keepDirty: true })
      }, 10)
    } catch (error) {
      // Detail fetch failed - keep search data, no error shown to user
      console.warn('KVK detail fetch failed, using search data only')
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        options={kvk.list || []}
        value={selectedValue}
        loading={kvk.loading}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Only update if it's an actual input change, not a reset
          if (reason === 'input') {
            setInputValue(newInputValue)
          } else if (reason === 'clear') {
            setInputValue('')
          }
        }}
        onChange={(_, value) => handleSelectCompany(value)}
        getOptionLabel={(option: IKvkCompany) => option.name || ''}
        isOptionEqualToValue={(option: IKvkCompany, value: IKvkCompany) => option.kvk_number === value.kvk_number}
        filterOptions={x => x} // Disable built-in filtering since we search via API
        renderInput={params => (
          <TextField
            {...params}
            label="Zoek bedrijf in KVK (naam of KVK-nummer)"
            placeholder="Typ om te zoeken..."
            InputProps={{
              ...params.InputProps,
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <>
                  {kvk.loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option: IKvkCompany) => (
          <Box
            component="li"
            {...props}>
            <Stack
              spacing={0.5}
              sx={{ width: '100%' }}>
              <Typography
                variant="body1"
                fontWeight={500}>
                {option.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                KVK: {option.kvk_number}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                {option.street} {option.house_number}
                {option.house_number_addition ? ` ${option.house_number_addition}` : ''}, {option.postal_code}{' '}
                {option.city}
              </Typography>
            </Stack>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Typ minimaal 2 karakters om te zoeken'
            : kvk.loading
            ? 'Zoeken...'
            : 'Geen resultaten gevonden'
        }
        clearOnBlur
        clearOnEscape
      />

      {/* Show default helper text when not showing other messages */}
      {(inputValue.length < 2 || kvk.loading) && kvk.list.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}>
          Of vul de gegevens handmatig in onderstaande velden
        </Typography>
      )}
    </Box>
  )
})
